/**
 * Geo Simple family renderers — 6 approved generators.
 * FAITHFULLY PORTED from geo-backgrounds.jsx reference.
 *
 * Signature: render(ctx, W, H, palette, params, rand)
 *   rand — seeded RNG function
 *   palette — { bg, colors, border, stroke } (already adjusted)
 *   params — { cellSize, density } (from PARAM_DEFS)
 *
 * The pick/randInt/shuffle helpers are imported from seededRandom.js
 */

import { pick, randInt, shuffle } from '../seededRandom';

// ═══════════════════════════════════════════════════════════
//  1. HALFTONE PANELS — drawHalftoneGrid (ref lines 173-222)
//  Panel grid (2-5 cols × 3-6 rows), each panel gets solid fill
//  + Ben-Day dots with 3 size-modulation patterns
// ═══════════════════════════════════════════════════════════

export function renderHalftonePanel(ctx, W, H, pal, P, rand) {
  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, 0, W, H);

  // Wire cellSize → grid dimensions, density → dot fill
  const cellSize = (P && P.cellSize) || 8;
  const density = (P && P.density) || 0.7;
  const cols = Math.max(1, Math.round(2 + (cellSize - 3) * 0.35));
  const rows = Math.max(2, Math.round(3 + (cellSize - 3) * 0.35));
  const gap = 8 + rand() * 8;
  const margin = 30 + rand() * 20;
  const cellW = (W - margin * 2 - gap * (cols - 1)) / cols;
  const cellH = (H - margin * 2 - gap * (rows - 1)) / rows;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = margin + col * (cellW + gap);
      const y = margin + row * (cellH + gap);
      const panelBg = pick(pal.colors, rand);
      const dotColor = pick(pal.colors.filter(c => c !== panelBg), rand) || pal.colors[0];

      ctx.fillStyle = panelBg;
      ctx.fillRect(x, y, cellW, cellH);

      const dotSize = 4 + density * 14;
      const dotGap = dotSize * (1.5 + (1 - density) * 2.0);
      const pattern = rand();

      ctx.fillStyle = dotColor;
      for (let dy = dotGap / 2; dy < cellH; dy += dotGap) {
        for (let dx = dotGap / 2; dx < cellW; dx += dotGap) {
          let sizeMod = 1;
          if (pattern < 0.33) {
            // Linear gradient: left to right
            sizeMod = dx / cellW;
          } else if (pattern < 0.66) {
            // Radial gradient: center to edge
            const cx2 = cellW / 2, cy2 = cellH / 2;
            const dist = Math.sqrt((dx - cx2) ** 2 + (dy - cy2) ** 2);
            sizeMod = 1 - dist / Math.sqrt(cx2 ** 2 + cy2 ** 2);
          } else {
            // Wave pattern
            sizeMod = 0.5 + 0.5 * Math.sin(dx * 0.05 + dy * 0.03);
          }
          const rad = dotSize * 0.5 * Math.max(0.1, sizeMod);
          ctx.beginPath();
          ctx.arc(x + dx, y + dy, rad, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      ctx.strokeStyle = pal.stroke || pal.bg;
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, cellW, cellH);
    }
  }

  ctx.strokeStyle = pal.border || pal.colors[0];
  ctx.lineWidth = 14;
  ctx.strokeRect(7, 7, W - 14, H - 14);
}

// ═══════════════════════════════════════════════════════════
//  2. BLOCK MAZE — drawBlockMaze (ref lines 272-333)
//  Voronoi-zoned block grid with directional texture hatching
// ═══════════════════════════════════════════════════════════

export function renderBlockMaze(ctx, W, H, pal, P, rand) {
  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, 0, W, H);

  // Wire cellSize → block dimensions, density → zone count and texture
  const cellSize = (P && P.cellSize) || 8;
  const density = (P && P.density) || 0.7;
  const blockSize = Math.round(20 + (20 - cellSize) * 3.5);
  const cols = Math.ceil(W / blockSize);
  const rows = Math.ceil(H / blockSize);
  const colors3 = shuffle(pal.colors, rand);
  const diagAngle = rand() > 0.5;
  const textureLines = density > 0.4;

  const zones = [];
  const numZones = Math.max(2, Math.round(3 + density * 8));
  for (let i = 0; i < numZones; i++) {
    zones.push({
      cx: rand() * W,
      cy: rand() * H,
      color: colors3[i % colors3.length],
      dir: rand() > 0.5 ? 0 : 1,
    });
  }

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = col * blockSize, y = row * blockSize;
      const px = x + blockSize / 2, py = y + blockSize / 2;

      // Find nearest Voronoi zone
      let minDist = Infinity, closest = zones[0];
      for (const z of zones) {
        const d = Math.sqrt((px - z.cx) ** 2 + (py - z.cy) ** 2);
        if (d < minDist) { minDist = d; closest = z; }
      }

      ctx.fillStyle = closest.color;
      ctx.fillRect(x, y, blockSize, blockSize);

      // Directional texture hatching per zone
      if (textureLines) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, y, blockSize, blockSize);
        ctx.clip();
        ctx.strokeStyle = ((pal.strokeHex || pal.bgHex || '#000000') + '40');
        ctx.lineWidth = 1;
        const step = 4 + rand() * 3;
        if (closest.dir === 0) {
          for (let ly = y; ly <= y + blockSize; ly += step) {
            ctx.beginPath();
            if (diagAngle) {
              ctx.moveTo(x, ly);
              ctx.lineTo(x + blockSize, ly - blockSize * 0.3);
            } else {
              ctx.moveTo(x, ly);
              ctx.lineTo(x + blockSize, ly);
            }
            ctx.stroke();
          }
        } else {
          for (let lx = x; lx <= x + blockSize; lx += step) {
            ctx.beginPath();
            ctx.moveTo(lx, y);
            ctx.lineTo(lx, y + blockSize);
            ctx.stroke();
          }
        }
        ctx.restore();
      }

      ctx.strokeStyle = pal.stroke || pal.bg;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(x, y, blockSize, blockSize);
    }
  }

  ctx.strokeStyle = pal.border || pal.colors[0];
  ctx.lineWidth = 12;
  ctx.strokeRect(6, 6, W - 12, H - 12);
}

// ═══════════════════════════════════════════════════════════
//  3. TRUCHET CURVES — drawTruchet (ref lines 336-369)
//  Classic quarter-circle Truchet tiles, clean border overlay
// ═══════════════════════════════════════════════════════════

export function renderTruchetCurves(ctx, W, H, pal, P, rand) {
  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, 0, W, H);

  // Wire cellSize → tile size, density → line weight and color variety
  const cellSize = (P && P.cellSize) || 8;
  const density = (P && P.density) || 0.7;
  const tileSize = Math.round(30 + (20 - cellSize) * 5);
  const cols = Math.ceil(W / tileSize) + 1;
  const rows = Math.ceil(H / tileSize) + 1;
  const lineW = 2 + density * 8;
  const colors4 = shuffle(pal.colors, rand);
  const numColors = Math.max(1, Math.round(density * 4));

  ctx.lineWidth = lineW;
  ctx.lineCap = 'round';

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = col * tileSize, y = row * tileSize;
      const flip = rand() > 0.5;
      ctx.strokeStyle = colors4[(row + col) % numColors];
      if (flip) {
        ctx.beginPath(); ctx.arc(x, y, tileSize / 2, 0, Math.PI / 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(x + tileSize, y + tileSize, tileSize / 2, Math.PI, Math.PI * 1.5); ctx.stroke();
      } else {
        ctx.beginPath(); ctx.arc(x + tileSize, y, tileSize / 2, Math.PI / 2, Math.PI); ctx.stroke();
        ctx.beginPath(); ctx.arc(x, y + tileSize, tileSize / 2, Math.PI * 1.5, Math.PI * 2); ctx.stroke();
      }
    }
  }

  // Border overlay — cover edge arcs with bg fill, then border stroke
  const bw = 12;
  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, 0, W, bw);
  ctx.fillRect(0, H - bw, W, bw);
  ctx.fillRect(0, 0, bw, H);
  ctx.fillRect(W - bw, 0, bw, H);
  ctx.strokeStyle = pal.border || pal.colors[0];
  ctx.lineWidth = bw;
  ctx.strokeRect(bw / 2, bw / 2, W - bw, H - bw);
}

// ═══════════════════════════════════════════════════════════
//  4. GEO COMPOSITION — drawGeoComposition (ref lines 372-428)
//  Recursive Mondrian-style subdivide, leaf cells get color
//  + one of 3 inner patterns (diagonal hatching, circle, horiz lines)
// ═══════════════════════════════════════════════════════════

export function renderGeoComposition(ctx, W, H, pal, P, rand) {
  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, 0, W, H);

  const cellSize = (P && P.cellSize) || 8;
  const density = (P && P.density) || 0.7;
  const colors5 = shuffle(pal.colors, rand);
  const margin = 40;
  const iw = W - margin * 2, ih = H - margin * 2;
  const maxDepth = Math.max(1, Math.round(1 + cellSize * 0.3));
  const minCell = Math.round(120 - density * 80);

  function subdivide(x, y, w, h, depth) {
    if (depth >= maxDepth || w < minCell || h < minCell) {
      // Leaf cell: fill with color
      ctx.fillStyle = pick(colors5, rand);
      ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = pal.stroke || pal.bg;
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, w, h);

      // Inner pattern
      const pat = rand();
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      if (pat < 0.3) {
        // Diagonal hatching
        ctx.strokeStyle = ((pal.strokeHex || pal.bgHex || '#000000') + '50');
        ctx.lineWidth = 1.5;
        const gap = 8 + rand() * 8;
        for (let d = -Math.max(w, h); d < Math.max(w, h) * 2; d += gap) {
          ctx.beginPath();
          ctx.moveTo(x + d, y);
          ctx.lineTo(x + d - h, y + h);
          ctx.stroke();
        }
      } else if (pat < 0.5) {
        // Circle
        ctx.fillStyle = pick(colors5, rand);
        const rad = Math.min(w, h) * 0.35;
        ctx.beginPath();
        ctx.arc(x + w / 2, y + h / 2, rad, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = pal.stroke || pal.bg;
        ctx.lineWidth = 2;
        ctx.stroke();
      } else if (pat < 0.65) {
        // Horizontal lines
        ctx.strokeStyle = ((pal.strokeHex || pal.bgHex || '#000000') + '40');
        ctx.lineWidth = 1.5;
        for (let ly = y + 6; ly < y + h; ly += 6) {
          ctx.beginPath();
          ctx.moveTo(x, ly);
          ctx.lineTo(x + w, ly);
          ctx.stroke();
        }
      }
      ctx.restore();
      return;
    }

    const horiz = rand() > 0.5;
    const split = 0.25 + rand() * 0.5;
    if (horiz) {
      const sy = y + h * split;
      subdivide(x, y, w, sy - y, depth + 1);
      subdivide(x, sy, w, y + h - sy, depth + 1);
    } else {
      const sx = x + w * split;
      subdivide(x, y, sx - x, h, depth + 1);
      subdivide(sx, y, x + w - sx, h, depth + 1);
    }
  }

  subdivide(margin, margin, iw, ih, 0);

  ctx.strokeStyle = pal.border || pal.colors[0];
  ctx.lineWidth = 14;
  ctx.strokeRect(7, 7, W - 14, H - 14);
}

// ═══════════════════════════════════════════════════════════
//  5. CONCENTRIC FRAMES — drawConcentricFrames (ref lines 99-170)
//  Nested rectangular frames with alternating V/H line hatching,
//  evenodd clipping, center colored rectangles, 10px border
// ═══════════════════════════════════════════════════════════

export function renderConcentricFrames(ctx, W, H, pal, P, rand) {
  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, 0, W, H);

  const cellSize = (P && P.cellSize) || 8;
  const density = (P && P.density) || 0.7;
  const layers = Math.max(2, Math.round(2 + cellSize * 0.4));
  const lineGap = Math.round(4 + (1 - density) * 14);
  const lineW = 1 + density * 3;
  const centerColor = pick(pal.colors, rand);
  const altColor = pick(pal.colors.filter(c => c !== centerColor), rand) || pal.colors[0];

  const marginX = W * 0.08;
  const marginY = H * 0.06;
  const stepX = (W / 2 - marginX) / layers;
  const stepY = (H / 2 - marginY) / layers;

  for (let i = 0; i < layers; i++) {
    const x = marginX + stepX * i;
    const y = marginY + stepY * i;
    const w = W - 2 * x;
    const h = H - 2 * y;
    const isVertical = i % 2 === 0;
    const nextX = i < layers - 1 ? marginX + stepX * (i + 1) : W / 2;
    const nextY = i < layers - 1 ? marginY + stepY * (i + 1) : H / 2;

    // Clip to band between this frame and next
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    if (i < layers - 1) {
      const nw = W - 2 * nextX;
      const nh = H - 2 * nextY;
      ctx.rect(nextX, nextY, nw, nh);
      ctx.clip('evenodd');
    } else {
      ctx.clip();
    }

    // Alternating vertical/horizontal hatching
    ctx.strokeStyle = pal.stroke || pal.bg;
    ctx.lineWidth = lineW;
    if (isVertical) {
      for (let lx = x; lx <= x + w; lx += lineGap) {
        ctx.beginPath(); ctx.moveTo(lx, y); ctx.lineTo(lx, y + h); ctx.stroke();
      }
    } else {
      for (let ly = y; ly <= y + h; ly += lineGap) {
        ctx.beginPath(); ctx.moveTo(x, ly); ctx.lineTo(x + w, ly); ctx.stroke();
      }
    }
    ctx.restore();

    // Frame stroke
    ctx.strokeStyle = pal.stroke || pal.bg;
    ctx.lineWidth = 2.5;
    ctx.strokeRect(x, y, w, h);
  }

  // Center colored rectangle
  const cw = W * (0.2 + rand() * 0.2);
  const ch = H * (0.15 + rand() * 0.15);
  const cx = (W - cw) / 2;
  const cy = (H - ch) / 2;
  ctx.fillStyle = centerColor;
  ctx.fillRect(cx, cy, cw, ch);
  ctx.strokeStyle = pal.stroke || pal.bg;
  ctx.lineWidth = 3;
  ctx.strokeRect(cx, cy, cw, ch);

  // Inner nested rectangle
  const icw = cw * 0.5, ich = ch * 0.5;
  ctx.fillStyle = altColor;
  ctx.fillRect((W - icw) / 2, (H - ich) / 2, icw, ich);
  ctx.strokeRect((W - icw) / 2, (H - ich) / 2, icw, ich);

  // Innermost small rectangle
  const tw = 20 + rand() * 20, th = 30 + rand() * 20;
  ctx.fillStyle = pal.bg;
  ctx.fillRect((W - tw) / 2, (H - th) / 2, tw, th);

  // Outer border
  ctx.strokeStyle = pal.border || pal.colors[0];
  ctx.lineWidth = 10;
  ctx.strokeRect(5, 5, W - 10, H - 10);
}

// ═══════════════════════════════════════════════════════════
//  6. SUNBURST — drawSunburstGrid (ref lines 225-269)
//  Full-canvas alternating color wedges, grid overlay, 14px border
// ═══════════════════════════════════════════════════════════

export function renderSunburst(ctx, W, H, pal, P, rand) {
  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, 0, W, H);

  const cellSize = (P && P.cellSize) || 8;
  const density = (P && P.density) || 0.7;
  const cx = W / 2, cy = H / 2;
  const segments = Math.max(6, Math.round(8 + cellSize * 1.5));
  const angleStep = (Math.PI * 2) / segments;
  const maxR = Math.sqrt(W * W + H * H);
  const colors2 = shuffle(pal.colors, rand);

  // Full-canvas alternating color wedges
  for (let i = 0; i < segments; i++) {
    const a1 = i * angleStep, a2 = a1 + angleStep;
    ctx.fillStyle = i % 2 === 0 ? colors2[0] : colors2[1 % colors2.length];
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a1) * maxR, cy + Math.sin(a1) * maxR);
    ctx.lineTo(cx + Math.cos(a2) * maxR, cy + Math.sin(a2) * maxR);
    ctx.closePath();
    ctx.fill();
  }

  // Grid overlay
  const gridSize = Math.round(30 + (1 - density) * 120);
  ctx.strokeStyle = (pal.bgHex || '#000000') + '60';
  ctx.lineWidth = 1;
  for (let x = 0; x <= W; x += gridSize) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
  }
  for (let y = 0; y <= H; y += gridSize) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
  }

  // Outer border
  ctx.strokeStyle = pal.border || pal.colors[0];
  ctx.lineWidth = 14;
  ctx.strokeRect(7, 7, W - 14, H - 14);
}
